/**
 * Meta WhatsApp Embedded Signup uses the Facebook JavaScript SDK.
 *
 * Dashboard checklist (also noted in backend envConfig.mjs):
 * - developers.facebook.com → your app → WhatsApp → Embedded Signup configuration (config_id).
 * - Facebook Login for Business: allowed domains and OAuth configurations for this origin.
 * - Permissions: whatsapp_business_management, whatsapp_business_messaging.
 * - Webhook: account_update (per Meta implementation guide).
 */

/** Standard OAuth for linking an existing WhatsApp / Business (dialog/oauth). */
const EXISTING_WHATSAPP_OAUTH_SCOPES = [
  'business_management',
  'whatsapp_business_management',
  'whatsapp_business_messaging',
].join(',');

/**
 * redirect_uri for dialog/oauth must exactly match Meta app "Valid OAuth Redirect URIs"
 * and the value sent to the server for code exchange. Set REACT_APP_7META_WHATSAPP_EXISTING_OAUTH_REDIRECT_URI in .env.
 */

/**
 * @see https://developers.facebook.com/docs/facebook-login/manually-build-a-login-flow
 */
export function buildExistingWhatsAppOAuthUrl({ clientId, redirectUri, state }) {
  const cid = String(clientId || '').trim();
  const redir = String(redirectUri || '').trim();
  const st = String(state || '').trim();
  if (!cid || !redir || !st) {
    throw new Error(
      'Missing client_id, redirect_uri, or state for WhatsApp existing-account OAuth.'
    );
  }
  const params = new URLSearchParams({
    client_id: cid,
    redirect_uri: redir,
    scope: EXISTING_WHATSAPP_OAUTH_SCOPES,
    response_type: 'code',
    state: st,
  });
  return `https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`;
}

export const EXISTING_WHATSAPP_OAUTH_STATE_PREFIX = 'wa_existing_oauth_state_';

/**
 * Configuration ID from Meta App Dashboard → Facebook Login for Business → Configurations
 * (create "WhatsApp Embedded Signup" from template). This is NOT your App ID.
 * Set REACT_APP_8FB_LOGIN_FOR_BUSINESS_CONFIG_ID and/or REACT_APP_9META_EMBEDDED_SIGNUP_CONFIG_ID in .env.
 */

const FB_MESSAGE_ORIGINS = new Set([
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://m.facebook.com',
]);

/**
 * @param {string} appId Meta / Facebook App ID (public).
 * @param {string} [version] Graph API version, e.g. v21.0
 * @returns {Promise<typeof window.FB>}
 */
export function initFacebookSdk(appId, version = 'v21.0') {
  return new Promise((resolve, reject) => {
    if (!appId) {
      reject(new Error('REACT_APP_1FACEBOOK_APP_ID is not set in .env'));
      return;
    }

    const bootstrap = () => {
      try {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version,
        });
        resolve(window.FB);
      } catch (e) {
        reject(e);
      }
    };

    if (window.FB) {
      bootstrap();
      return;
    }

    window.fbAsyncInit = bootstrap;

    const id = 'facebook-jssdk';
    if (document.getElementById(id)) {
      const deadline = Date.now() + 20000;
      const t = setInterval(() => {
        if (window.FB) {
          clearInterval(t);
          bootstrap();
        } else if (Date.now() > deadline) {
          clearInterval(t);
          reject(new Error('Facebook SDK load timeout'));
        }
      }, 50);
      return;
    }

    const js = document.createElement('script');
    js.id = id;
    js.async = true;
    js.defer = true;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    document.body.appendChild(js);
  });
}

/**
 * Options Meta documents for WhatsApp Embedded Signup (Facebook Login for Business).
 * @see https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation/
 */
export function buildEmbeddedSignupLoginOptions(configIdRaw) {
  const config_id = String(configIdRaw ?? '').trim();
  if (!config_id) {
    throw new Error(
      'Embedded Signup requires config_id. Set REACT_APP_8FB_LOGIN_FOR_BUSINESS_CONFIG_ID or REACT_APP_9META_EMBEDDED_SIGNUP_CONFIG_ID in frontend/.env, then restart the dev server or rebuild.'
    );
  }
  return {
    config_id,
    response_type: 'code',
    override_default_response_type: true,
    extras: { setup: {}, sessionInfoVersion: '3' },
  };
}

/**
 * Launch Embedded Signup (WhatsApp) using a Facebook Login for Business config_id.
 * @param {string} configId From Meta App Dashboard → Embedded Signup configuration.
 */
export function loginWhatsAppEmbeddedSignup(configId) {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not initialized'));
      return;
    }

    let options;
    try {
      options = buildEmbeddedSignupLoginOptions(configId);
    } catch (e) {
      reject(e);
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.info(
        '[Embedded Signup] FB.login with config_id length:',
        options.config_id.length
      );
    }

    window.FB.login((response) => {
      resolve(response);
    }, options);
  });
}

/**
 * Listen for WA_EMBEDDED_SIGNUP postMessage events from Facebook.
 * @param {(data: object) => void} onMessage
 * @returns {() => void} unsubscribe
 */
export function subscribeWhatsAppEmbeddedSignupMessages(onMessage) {
  const handler = (event) => {
    if (!FB_MESSAGE_ORIGINS.has(event.origin)) return;
    let payload = event.data;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return;
      }
    }
    if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;
    onMessage(payload);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

/**
 * Merge postMessage payload and FB.login auth response into fields for the backend.
 */
export function extractEmbeddedSignupFields(fbLoginResponse, postMessagePayload) {
  const fromMsg = postMessagePayload || {};
  const data = fromMsg.data || {};
  const auth = fbLoginResponse?.authResponse || {};

  const code =
    fromMsg.code ||
    data.code ||
    fromMsg.authorization_code ||
    data.authorization_code ||
    auth.code ||
    fbLoginResponse?.code;

  return {
    metaCode: code || null,
    wabaId: data.waba_id || data.wabaId || fromMsg.waba_id || fromMsg.wabaId || null,
    phoneNumberId:
      data.phone_number_id ||
      data.phoneNumberId ||
      fromMsg.phone_number_id ||
      fromMsg.phoneNumberId ||
      null,
    businessPortfolioId:
      data.business_portfolio_id ||
      data.businessPortfolioId ||
      fromMsg.business_portfolio_id ||
      fromMsg.businessPortfolioId ||
      null,
  };
}
